// Conversational policy engine. Turns a plain-English description into a
// structured policy and checks it against existing policies for contradictions.
// Deterministic stand-in for an LLM that would emit the same structured object
// and reasoning; the heuristics are tuned for the demo's policy vocabulary.

// Subject = the control the policy is about. Used to decide whether two
// policies are "about the same thing" when checking for conflicts.
const SUBJECTS = [
  { key: 'card_on_file', label: 'card on file', test: /card[\s-]?on[\s-]?file|credit card|\bcof\b/ },
  { key: 'collect_estimate', test: /collect|estimate|upfront|deposit|prepay|pre-?pay|patient responsibility|at check-?in/, label: 'collect upfront' },
  { key: 'referral', test: /referral|gatekeeper|\bpcp\b/, label: 'referral' },
  { key: 'abn', test: /\babn\b|advance beneficiary|non-?covered|medicare.*(procedure|waiver)/, label: 'ABN' },
  { key: 'prior_auth', test: /prior[\s-]?auth|authorization|pre-?cert|precert/, label: 'prior authorization' },
  { key: 'verify_eligibility', test: /verif|eligibility check|run eligibility|re-?check|re-?verif/, label: 'eligibility verification' },
  { key: 'inactive_hold', test: /inactive|coverage (end|lapse|term)|termed|lapsed|no coverage/, label: 'inactive coverage' },
];

const WAIVE = /\b(do not|don'?t|never|no longer|without|waive|waiv|skip|exempt|bypass|stop|remove|allow .* without|no need)\b/;
const REQUIRE = /\b(require|must|always|enforce|block|hold|need|mandate|ensure|don'?t allow|prevent)\b/;

function classify(text) {
  const t = (text || '').toLowerCase();
  const subject = SUBJECTS.find((s) => s.test.test(t)) || { key: 'generic', label: 'this control' };
  const intent = WAIVE.test(t) ? 'waive' : 'require';
  const m = t.match(/\$\s?([\d][\d,]*)/);
  const threshold = m ? parseInt(m[1].replace(/,/g, ''), 10) : null;
  return { subject: subject.key, subjectLabel: subject.label, intent, threshold };
}

function scopeFor(text, subjectKey) {
  const t = (text || '').toLowerCase();
  if (/high[\s-]?deductible|hdhp/.test(t)) return 'HDHP / high-deductible visits';
  if (/\bhmo\b|\bepo\b|gatekeeper/.test(t)) return 'HMO / EPO plans';
  if (/medicare/.test(t)) return 'Medicare';
  if (/\bppo\b/.test(t)) return 'PPO plans';
  if (/inactive|lapsed|termed/.test(t)) return 'Inactive coverage';
  if (/new patient/.test(t)) return 'New patients';
  if (/all (visits|appointments|patients|encounters)/.test(t)) return 'All visits';
  const bySubject = { referral: 'HMO / EPO plans', abn: 'Medicare procedures', card_on_file: 'HDHP / high-deductible visits', inactive_hold: 'Inactive coverage', verify_eligibility: 'All appointments', prior_auth: 'Procedures requiring auth' };
  return bySubject[subjectKey] || 'All visits';
}

function enforcementFor(text) {
  const t = (text || '').toLowerCase();
  if (/\b(turn off|disable|deactivate|do not enforce|don'?t enforce|inactive policy|pause)\b/.test(t)) return 'off';
  if (/\b(enforce|block|hold|must|mandate|prevent|require|don'?t allow|do not allow)\b/.test(t)) return 'enforce';
  if (/\b(warn|alert|remind|prompt|flag|notify|suggest|recommend)\b/.test(t)) return 'warn';
  return 'warn';
}

const titleCase = (s) => s.replace(/\b\w/g, (c) => c.toUpperCase());

function nameFor(text, subjectKey, threshold) {
  const dollar = threshold ? ` over $${threshold.toLocaleString()}` : '';
  const bySubject = {
    card_on_file: `Card on file${dollar}`,
    collect_estimate: `Collect estimate upfront${dollar}`,
    referral: 'Confirm referral for gatekeeper plans',
    abn: 'Signed ABN for Medicare procedures',
    prior_auth: 'Prior authorization required',
    verify_eligibility: 'Verify eligibility before the visit',
    inactive_hold: 'Hold scheduling on inactive coverage',
  };
  if (bySubject[subjectKey]) return bySubject[subjectKey];
  const words = (text || '').trim().replace(/[.!?]+$/, '').split(/\s+/).slice(0, 7).join(' ');
  return titleCase(words || 'New policy');
}

// Parse plain English → a structured, editable policy draft.
export function parsePolicy(text) {
  const c = classify(text);
  const clean = (text || '').trim();
  const description = clean ? clean[0].toUpperCase() + clean.slice(1) + (/[.!?]$/.test(clean) ? '' : '.') : '';
  return {
    name: nameFor(text, c.subject, c.threshold),
    description,
    scope: scopeFor(text, c.subject),
    enforcement: enforcementFor(text),
    // hidden analysis fields used for conflict detection
    subject: c.subject,
    subjectLabel: c.subjectLabel,
    intent: c.intent,
    threshold: c.threshold,
  };
}

const verb = (intent) => (intent === 'waive' ? 'waives / skips' : 'requires');

// Normalized plan/visit family from a scope string. Two policies only conflict
// when their scopes can actually overlap (same family, or one is broad).
function planFamily(scope) {
  const t = (scope || '').toLowerCase();
  if (/hdhp|high[\s-]?deductible/.test(t)) return 'hdhp';
  if (/\bhmo\b|\bepo\b|gatekeeper/.test(t)) return 'hmo_epo';
  if (/medicare/.test(t)) return 'medicare';
  if (/\bppo\b/.test(t)) return 'ppo';
  if (/inactive|lapse|term/.test(t)) return 'inactive';
  if (/new patient/.test(t)) return 'new_patient';
  if (/all\b/.test(t)) return 'all';
  return 'other';
}
const scopesOverlap = (a, b) => {
  const fa = planFamily(a), fb = planFamily(b);
  return fa === fb || fa === 'all' || fb === 'all' || fa === 'other' || fb === 'other';
};

// Compare a draft against existing policies. Returns an array of
// { severity: 'contradiction' | 'overlap', policy, reason }.
export function findConflicts(draft, existing = []) {
  if (!draft || draft.subject === 'generic' || draft.enforcement === 'off') return [];
  const out = [];
  for (const p of existing) {
    if (p.enabled === false) continue;
    const c = classify(`${p.name} ${p.description} ${p.scope}`);
    if (c.subject !== draft.subject) continue;
    // Only conflict when the two policies' scopes can actually overlap.
    if (!scopesOverlap(draft.scope, p.scope)) continue;

    // Opposing intent on the same control = hard contradiction.
    if (c.intent !== draft.intent) {
      out.push({
        severity: 'contradiction',
        policy: p,
        reason: `“${p.name}” ${verb(c.intent)} ${c.subjectLabel}, but this policy ${verb(draft.intent)} it. The two directly conflict.`,
      });
      continue;
    }
    // Same intent but different dollar threshold = conflicting thresholds.
    if (draft.threshold && c.threshold && draft.threshold !== c.threshold) {
      out.push({
        severity: 'contradiction',
        policy: p,
        reason: `“${p.name}” already governs ${c.subjectLabel} at a $${c.threshold.toLocaleString()} threshold; this sets a different $${draft.threshold.toLocaleString()} threshold for the same control.`,
      });
      continue;
    }
    // Same control, same intent, overlapping scope = redundant.
    out.push({
      severity: 'overlap',
      policy: p,
      reason: `Overlaps “${p.name}”, which already covers ${c.subjectLabel}. Consider editing that policy instead of adding a second one.`,
    });
  }
  // Sort contradictions first.
  return out.sort((a, b) => (a.severity === 'contradiction' ? -1 : 1) - (b.severity === 'contradiction' ? -1 : 1));
}
